import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, CircleIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { connectSections } from '../../data/connect';
import { programInfo } from '../../data/program';
import { useProto } from '../../contexts/PrototypeContext';
import { StaffingRolesSection } from './StaffingRolesSection';
import { DiscountCapacitySection } from './DiscountCapacitySection';
import { CoachCreditSetting } from './CoachCreditSetting';
export function ProgramDrawer() {
  const {
    state
  } = useProto();
  const [section, setSection] = useState('staffing');
  const scrollRef = useRef<HTMLDivElement>(null);
  const idx = connectSections.findIndex(s => s.id === section);
  const current = connectSections[idx];
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0
    });
  }, [section, state.activeScenario]);
  const {
    config
  } = state;
  const hasErrors = config.discountEnabled && (!(config.amount > 0) || config.roles.some(r => r.opportunities < r.claimed));
  function save(label: string) {
    if (hasErrors) {
      toast.error('Fix the highlighted fields in Discount & Capacity Management before saving.');
      return;
    }
    toast.success(label);
  }
  return <div role="dialog" aria-modal="true" aria-labelledby="drawer-title" className="absolute inset-y-0 right-0 flex w-[min(1100px,90%)] flex-col bg-white shadow-2xl" data-id="element-94" data-mp="2av0eilr2psxq">
      <header className="flex items-start justify-between border-b-2 border-connect-blue px-7 pb-3 pt-4" data-id="element-95" data-mp="21ulhwq8nsrex">
        <div data-id="element-96" data-mp="26wkm22c83scx">
          <h1 id="drawer-title" className="text-[26px] text-connect-ink" data-id="element-97" data-mp="26t6iiqrtpd3w">Edit Program</h1>
          <p className="text-sm text-connect-muted" data-id="element-98" data-mp="1rztmnt2jugnb">
            {programInfo.name} · Program {programInfo.id} · {programInfo.franchise}
          </p>
        </div>
        <button type="button" aria-label="Close" onClick={() => toast('Closing is disabled in this prototype.')} className="rounded p-1 text-connect-blue transition-colors hover:bg-connect-blueSoft" data-id="element-99" data-mp="3md2wjlz0c463">
          <XIcon className="h-6 w-6" data-id="element-100" data-mp="3puyxqygy7nl2" />
        </button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[210px_minmax(0,1fr)]" data-id="element-101" data-mp="3kv480y8wt41l">
        <nav aria-label="Program sections" className="overflow-y-auto border-r border-connect-line px-3 py-5" data-id="element-102" data-mp="3kx77e8vyc9iz">
          <ul className="space-y-0.5" data-id="element-103" data-mp="3bu656ja7j2kl">
            {connectSections.map(s => {
            const active = s.id === section;
            return <li key={s.id} data-id="element-104" data-mp="cej96a9zrrc7">
                  <button type="button" onClick={() => setSection(s.id)} aria-current={active ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-[15px] transition-colors duration-150 ${active ? 'bg-connect-blueSoft font-medium text-connect-ink' : 'text-connect-muted hover:bg-connect-canvas'}`} data-id="element-105" data-mp="cgvpyk40jvno">
                    {s.id === 'staffing' && hasErrors ? <AlertTriangleIcon className="h-[18px] w-[18px] text-amber-600" aria-label="Has errors" data-id="element-106" data-mp="292lnftsxln98" /> : s.complete ? <CheckCircle2Icon className="h-[18px] w-[18px] text-connect-blue" aria-label="Complete" data-id="element-107" data-mp="2i28h1tff5orq" /> : <CircleIcon className="h-[18px] w-[18px] text-gray-400" aria-hidden="true" data-id="element-108" data-mp="2n1lch2kcw45x" />}
                    {s.label}
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <div ref={scrollRef} className="overflow-y-auto px-8 py-6" data-id="element-109" data-mp="2wx6a6oie6i9v">
          {section === 'staffing' ? <div className="mx-auto max-w-[820px] space-y-10 pb-10" data-id="element-110" data-mp="35rtchbsreehw">
              <div data-id="element-111" data-mp="35r1fbwjik8pi">
                <h2 className="text-xl font-medium text-connect-ink" data-id="element-112" data-mp="35tnmvw6emo7q">Staffing</h2>
                <p className="mt-1 text-sm text-connect-muted" data-id="element-113" data-mp="3asqqrdaneoe4">Volunteer roles this program needs and the incentives offered to families who volunteer.</p>
              </div>
              <StaffingRolesSection data-id="element-114" data-mp="321yze3qhvz3i" />
              <DiscountCapacitySection data-id="element-115" data-mp="320xc90w7fm5f" />
              <CoachCreditSetting data-id="element-116" data-mp="2x4ysjqltmsbc" />
            </div> : <div className="mx-auto mt-16 max-w-md text-center" data-id="element-117" data-mp="3bt3dl18cjfxa">
              <h2 className="text-lg font-medium text-connect-ink" data-id="element-118" data-mp="3bsbhzqer1i70">{current.label}</h2>
              <p className="mt-2 text-sm text-connect-muted" data-id="element-119" data-mp="36uie540x509w">This section is outside the scope of this prototype. Go to Staffing to review the Volunteer Registration Discount.</p>
              <button type="button" onClick={() => setSection('staffing')} className="mt-4 rounded border border-connect-blue px-4 py-2 text-sm font-medium text-connect-blue transition-colors hover:bg-connect-blueSoft" data-id="element-120" data-mp="2e6e6icysed4k">
                Go to Staffing
              </button>
            </div>}
        </div>
      </div>

      <footer className="grid grid-cols-4 gap-3 border-t border-connect-line px-6 py-4 text-[15px] font-medium uppercase tracking-wide" data-id="element-121" data-mp="8atetw9x8zcx">
        <button type="button" onClick={() => toast('Changes discarded.')} className="rounded border border-connect-danger py-3 text-connect-danger transition-colors hover:bg-connect-dangerSoft" data-id="element-122" data-mp="awsonh1br60f">
          Cancel
        </button>
        <button type="button" disabled={idx === 0} onClick={() => setSection(connectSections[idx - 1].id)} className="rounded border border-connect-line py-3 text-connect-muted transition-colors hover:bg-connect-canvas disabled:opacity-50" data-id="element-123" data-mp="1ww7e0h9l9fe">
          Back
        </button>
        <button type="button" onClick={() => save('Program saved. Changes apply to new registrations.')} className="rounded border border-connect-blue py-3 text-connect-blue transition-colors hover:bg-connect-blueSoft" data-id="element-124" data-mp="ym3sx8hvvijb">
          Save &amp; Exit
        </button>
        <button type="button" disabled={idx === connectSections.length - 1} onClick={() => {
        if (section === 'staffing' && hasErrors) return save('');
        setSection(connectSections[idx + 1].id);
      }} className="rounded bg-connect-blue py-3 text-white shadow transition-colors hover:bg-connect-blueDark disabled:opacity-50" data-id="element-125" data-mp="our28k7tmyev">
          Next
        </button>
      </footer>
    </div>;
}